import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { AuditAction, AuditResourceType, Profile } from '@ellie/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditLogRow {
  id: string;
  admin_id: string;
  action: AuditAction;
  resource_type: AuditResourceType;
  resource_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  profiles: Pick<Profile, 'full_name' | 'email'> | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTION_LABELS: Record<AuditAction, string> = {
  generate_workbook: 'Generate Workbook',
  deliver_order: 'Deliver Order',
  delete_order: 'Delete Order',
  retry_delivery: 'Retry Delivery',
  retry_generation: 'Retry Generation',
  approve_workbook: 'Approve Workbook',
  reject_workbook: 'Reject Workbook',
  recover_stuck_orders: 'Recover Stuck Orders',
  upload_community_photo: 'Upload Community Photo',
  activate_community_photo: 'Activate Community Photo',
  deactivate_community_photo: 'Deactivate Community Photo',
  delete_community_photo: 'Delete Community Photo',
};

const ACTION_COLORS: Record<AuditAction, string> = {
  generate_workbook: 'bg-purple-100 text-purple-700',
  deliver_order: 'bg-green-100 text-green-700',
  delete_order: 'bg-red-100 text-red-700',
  retry_delivery: 'bg-orange-100 text-orange-700',
  retry_generation: 'bg-purple-100 text-purple-700',
  approve_workbook: 'bg-emerald-100 text-emerald-700',
  reject_workbook: 'bg-red-100 text-red-700',
  recover_stuck_orders: 'bg-yellow-100 text-yellow-700',
  upload_community_photo: 'bg-blue-100 text-blue-700',
  activate_community_photo: 'bg-green-100 text-green-700',
  deactivate_community_photo: 'bg-gray-100 text-gray-700',
  delete_community_photo: 'bg-red-100 text-red-700',
};

const ALL_ACTIONS: AuditAction[] = [
  'generate_workbook',
  'deliver_order',
  'delete_order',
  'retry_delivery',
  'retry_generation',
  'approve_workbook',
  'reject_workbook',
  'recover_stuck_orders',
  'upload_community_photo',
  'activate_community_photo',
  'deactivate_community_photo',
  'delete_community_photo',
];

type DateRange = '7' | '30' | '90' | 'all';

const DATE_RANGES: { value: DateRange; label: string }[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateId(id: string | null): string {
  if (!id) return '—';
  return id.slice(0, 8);
}

function formatMetadata(metadata: Record<string, unknown>): string {
  const entries = Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );
  if (entries.length === 0) return '—';
  return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [actionFilter, setActionFilter] = useState<AuditAction | 'all'>('all');
  const [dateRange, setDateRange] = useState<DateRange>('30');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchEntries = useCallback(async () => {
    setLoading(true);

    let query = supabase
      .from('admin_audit_log')
      .select('*, profiles(full_name, email)')
      .order('created_at', { ascending: false })
      .limit(200);

    if (actionFilter !== 'all') {
      query = query.eq('action', actionFilter);
    }

    if (dateRange !== 'all') {
      const days = parseInt(dateRange, 10);
      const since = new Date();
      since.setDate(since.getDate() - days);
      query = query.gte('created_at', since.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error('Failed to fetch audit log:', error.message);
    }

    setEntries((data as AuditLogRow[]) ?? []);
    setLoading(false);
  }, [actionFilter, dateRange]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Client-side search filter (by admin name or email)
  const filtered = searchQuery.trim()
    ? entries.filter((entry) => {
        const q = searchQuery.toLowerCase();
        const name = entry.profiles?.full_name?.toLowerCase() ?? '';
        const email = entry.profiles?.email?.toLowerCase() ?? '';
        return name.includes(q) || email.includes(q);
      })
    : entries;

  return (
    <div className="p-6">
      <h1 className="mb-6 font-heading text-2xl text-text-primary">Audit Log</h1>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* Action filter */}
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value as AuditAction | 'all')}
          className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        >
          <option value="all">All actions</option>
          {ALL_ACTIONS.map((action) => (
            <option key={action} value={action}>
              {ACTION_LABELS[action]}
            </option>
          ))}
        </select>

        {/* Date range */}
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRange)}
          className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        >
          {DATE_RANGES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        {/* Search by admin */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search admin name…"
          className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-text-muted">No audit log entries found.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-gray-50 text-xs font-medium uppercase tracking-wider text-text-muted">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Resource</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-border-light transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-text-muted">
                    {formatDateTime(entry.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-text-primary">
                      {entry.profiles?.full_name ?? 'Unknown'}
                    </p>
                    <p className="text-xs text-text-muted">{entry.profiles?.email ?? ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    <ActionBadge action={entry.action} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="capitalize text-text-muted">{entry.resource_type}</span>
                    <span className="ml-1 font-mono text-xs text-text-light">
                      {truncateId(entry.resource_id)}
                    </span>
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-xs text-text-muted">
                    {formatMetadata(entry.metadata)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action Badge
// ---------------------------------------------------------------------------

function ActionBadge({ action }: { action: AuditAction }) {
  const color = ACTION_COLORS[action] ?? 'bg-gray-100 text-gray-700';
  const label = ACTION_LABELS[action] ?? action;

  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${color}`}
    >
      {label}
    </span>
  );
}
